import { useConversation } from '@elevenlabs/react'
import { useCallback, useEffect, useRef } from 'react'
import { BEN_AGENT_ID } from './config'

const BEN_CONNECTION_DELAY = { default: 2000, android: 3000, ios: 2000 }
const DEFAULT_CONNECTION_TYPE = 'websocket'

const isIOS = () =>
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))

const trackBenEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, {
      event_category: 'ben_ai_assistant',
      event_label: 'elevenlabs_sdk',
      ...params,
    })
  }
}

const getBenConnectionType = () => {
  if (typeof window === 'undefined') return DEFAULT_CONNECTION_TYPE

  const rawTransport = new URLSearchParams(window.location.search)
    .get('ben_transport')
    ?.toLowerCase()

  if (rawTransport === 'webrtc' || rawTransport === 'rtc') return 'webrtc'
  if (rawTransport === 'websocket' || rawTransport === 'ws') return 'websocket'
  return DEFAULT_CONNECTION_TYPE
}

const getToolName = (payload) =>
  payload?.tool_name ||
  payload?.toolName ||
  payload?.name ||
  payload?.tool?.name ||
  payload?.request?.tool_name ||
  'unknown'

const BEN_DIAGNOSTICS_STORAGE_KEY = 'ben.diagnostics.latest'
const BEN_DIAGNOSTICS_MAX_EVENTS = 260

const createDiagnosticsState = () => ({
  enabled: true,
  verbose: false,
  events: [],
  sequence: 0,
  sessionStartedAt: null,
  sessionMeta: {},
  conversationId: null,
  status: null,
  mode: null,
  lastAgentText: '',
  lastUserText: '',
  currentSpeechTurn: null,
  lastCompletedSpeechTurn: null,
})

const benDiagnosticsState = createDiagnosticsState()

const safeNow = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()

const getIsoTime = () => new Date().toISOString()

const roundMs = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : null

const trimText = (value, maxLength = 120) => {
  if (typeof value !== 'string') return ''
  if (value.length <= maxLength) return value
  return `${value.slice(0, maxLength - 1)}…`
}

const safeStringify = (value) => {
  const seen = new WeakSet()
  return JSON.stringify(value, (key, item) => {
    if (item instanceof Error) {
      return {
        name: item.name,
        message: item.message,
        stack: item.stack,
      }
    }
    if (typeof item === 'string' && item.length > 1000) {
      return trimText(item, 1000)
    }
    if (item && typeof item === 'object') {
      if (seen.has(item)) return '[Circular]'
      seen.add(item)
    }
    return item
  })
}

const isQuestionLike = (value) => /[?？]\s*$/.test(value || '')

const estimateBase64Bytes = (value) => {
  if (typeof value !== 'string') return 0
  const padding = value.endsWith('==') ? 2 : value.endsWith('=') ? 1 : 0
  return Math.max(0, Math.floor((value.length * 3) / 4) - padding)
}

const getPlatformDiagnostics = () => {
  if (typeof window === 'undefined') return {}
  return {
    href: window.location.href,
    userAgent: window.navigator?.userAgent || '',
    platform: window.navigator?.platform || '',
    maxTouchPoints: window.navigator?.maxTouchPoints || 0,
    isIOS: isIOS(),
  }
}

const persistBenDiagnostics = () => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage?.setItem(
      BEN_DIAGNOSTICS_STORAGE_KEY,
      safeStringify({
        sessionStartedAt: benDiagnosticsState.sessionStartedAt,
        sessionMeta: benDiagnosticsState.sessionMeta,
        conversationId: benDiagnosticsState.conversationId,
        status: benDiagnosticsState.status,
        mode: benDiagnosticsState.mode,
        lastAgentText: benDiagnosticsState.lastAgentText,
        lastUserText: benDiagnosticsState.lastUserText,
        lastCompletedSpeechTurn: benDiagnosticsState.lastCompletedSpeechTurn,
        events: benDiagnosticsState.events,
      })
    )
  } catch (error) {
    console.warn('[Ben][diag] persist failed', error)
  }
}

const recordBenDiagnostic = (type, data = {}, options = {}) => {
  if (!benDiagnosticsState.enabled) return null

  const event = {
    seq: ++benDiagnosticsState.sequence,
    type,
    at: getIsoTime(),
    t: roundMs(safeNow()),
    conversationId: benDiagnosticsState.conversationId,
    mode: benDiagnosticsState.mode,
    ...data,
  }

  benDiagnosticsState.events.push(event)
  if (benDiagnosticsState.events.length > BEN_DIAGNOSTICS_MAX_EVENTS) {
    benDiagnosticsState.events.splice(
      0,
      benDiagnosticsState.events.length - BEN_DIAGNOSTICS_MAX_EVENTS
    )
  }

  const shouldLog =
    options.forceLog ||
    type !== 'audio_chunk' ||
    benDiagnosticsState.verbose

  if (shouldLog) {
    const logger = options.warn ? console.warn : console.debug
    logger('[Ben][diag]', type, event)
  }

  if (options.persist) persistBenDiagnostics()
  return event
}

const resetBenDiagnostics = (sessionMeta = {}) => {
  const fresh = createDiagnosticsState()
  Object.assign(benDiagnosticsState, fresh, {
    sessionStartedAt: getIsoTime(),
    sessionMeta: {
      sdk: '@elevenlabs/react 1.6.4 + @elevenlabs/client 1.9.0',
      ...getPlatformDiagnostics(),
      ...sessionMeta,
    },
  })
  recordBenDiagnostic('session_start', benDiagnosticsState.sessionMeta, {
    forceLog: true,
    persist: true,
  })
}

const finishCurrentSpeechTurn = (reason) => {
  const turn = benDiagnosticsState.currentSpeechTurn
  if (!turn) return null

  const endedAt = safeNow()
  const summary = {
    reason,
    startedAt: roundMs(turn.startedAt),
    endedAt: roundMs(endedAt),
    durationMs: roundMs(endedAt - turn.startedAt),
    audioChunks: turn.audioChunks,
    audioBytes: turn.audioBytes,
    lastAudioGapMs: turn.lastAudioAt ? roundMs(endedAt - turn.lastAudioAt) : null,
    alignmentEvents: turn.alignmentEvents,
    lastAlignmentTail: turn.lastAlignmentTail,
    lastAlignmentDurationMs: turn.lastAlignmentDurationMs,
    agentTextTail: trimText(turn.agentText || benDiagnosticsState.lastAgentText, 180),
    questionLike: isQuestionLike(turn.agentText || benDiagnosticsState.lastAgentText),
  }

  benDiagnosticsState.lastCompletedSpeechTurn = summary
  benDiagnosticsState.currentSpeechTurn = null

  recordBenDiagnostic('speech_turn_complete', summary, {
    forceLog: true,
    persist: true,
  })

  trackBenEvent('ben_speech_turn_complete', {
    question_like: summary.questionLike ? 'yes' : 'no',
    audio_chunks: summary.audioChunks,
    has_alignment: summary.alignmentEvents > 0 ? 'yes' : 'no',
    last_audio_gap_ms: summary.lastAudioGapMs,
  })

  return summary
}

const startSpeechTurn = () => {
  finishCurrentSpeechTurn('new_speaking_mode')
  benDiagnosticsState.currentSpeechTurn = {
    startedAt: safeNow(),
    audioChunks: 0,
    audioBytes: 0,
    lastAudioAt: null,
    alignmentEvents: 0,
    lastAlignmentTail: '',
    lastAlignmentDurationMs: null,
    agentText: benDiagnosticsState.lastAgentText,
  }
}

const summarizeAlignment = (alignment) => {
  const chars = Array.isArray(alignment?.chars) ? alignment.chars : []
  const starts = Array.isArray(alignment?.char_start_times_ms)
    ? alignment.char_start_times_ms
    : []
  const durations = Array.isArray(alignment?.char_durations_ms)
    ? alignment.char_durations_ms
    : []
  const lastIndex = chars.length - 1
  const lastStart = starts[lastIndex]
  const lastDuration = durations[lastIndex]
  const durationMs =
    typeof lastStart === 'number' && typeof lastDuration === 'number'
      ? lastStart + lastDuration
      : null

  return {
    charCount: chars.length,
    tail: trimText(chars.join('').slice(-90), 90),
    durationMs: roundMs(durationMs),
    lastChar: chars[lastIndex] || '',
  }
}

const installBenDiagnosticsApi = () => {
  if (typeof window === 'undefined') return
  if (window.benDiagnostics?.__installed) return

  window.benDiagnostics = {
    __installed: true,
    enable: () => {
      benDiagnosticsState.enabled = true
      persistBenDiagnostics()
      return window.benDiagnostics.dump()
    },
    disable: () => {
      benDiagnosticsState.enabled = false
      persistBenDiagnostics()
    },
    verbose: (value = true) => {
      benDiagnosticsState.verbose = Boolean(value)
      return benDiagnosticsState.verbose
    },
    clear: () => {
      benDiagnosticsState.events = []
      benDiagnosticsState.lastCompletedSpeechTurn = null
      persistBenDiagnostics()
    },
    dump: () => ({
      sessionStartedAt: benDiagnosticsState.sessionStartedAt,
      sessionMeta: benDiagnosticsState.sessionMeta,
      conversationId: benDiagnosticsState.conversationId,
      status: benDiagnosticsState.status,
      mode: benDiagnosticsState.mode,
      lastAgentText: benDiagnosticsState.lastAgentText,
      lastUserText: benDiagnosticsState.lastUserText,
      currentSpeechTurn: benDiagnosticsState.currentSpeechTurn,
      lastCompletedSpeechTurn: benDiagnosticsState.lastCompletedSpeechTurn,
      events: [...benDiagnosticsState.events],
    }),
    latest: () => {
      try {
        return JSON.parse(
          window.localStorage?.getItem(BEN_DIAGNOSTICS_STORAGE_KEY) || 'null'
        )
      } catch {
        return null
      }
    },
    copy: async () => {
      const dump = JSON.stringify(window.benDiagnostics.dump(), null, 2)
      await window.navigator?.clipboard?.writeText(dump)
      return dump
    },
  }
}

export function useBen({ onTranscript } = {}) {
  const unlockCtxRef = useRef(null)

  const conv = useConversation({
    onConnect: ({ conversationId } = {}) => {
      benDiagnosticsState.conversationId = conversationId || null
      recordBenDiagnostic('connect', { conversationId }, { forceLog: true })
      console.log('[Ben] connected', conversationId)
      trackBenEvent('ben_conversation_connected')
    },
    onDisconnect: (d) => {
      finishCurrentSpeechTurn('disconnect')
      recordBenDiagnostic('disconnect', { details: d }, { forceLog: true, persist: true })
      console.log('[Ben] disconnected', d)
    },
    onError: (err, ctx) => {
      recordBenDiagnostic('error', { error: err, context: ctx }, { warn: true, persist: true })
      console.error('[Ben] error', err, ctx)
    },
    onMessage: (m) => {
      const role = m.role || (m.source === 'ai' ? 'agent' : m.source)
      const text = m.message || ''
      if (role === 'agent') {
        benDiagnosticsState.lastAgentText = text
        if (benDiagnosticsState.currentSpeechTurn) {
          benDiagnosticsState.currentSpeechTurn.agentText = text
        }
      } else if (role === 'user') {
        benDiagnosticsState.lastUserText = text
      }
      recordBenDiagnostic('message', {
        role,
        eventId: m.event_id,
        textLength: text.length,
        textTail: trimText(text, 180),
        questionLike: role === 'agent' ? isQuestionLike(text) : undefined,
      })
      console.log(`[Ben] ${m.source} →`, m.message)
      onTranscript?.(m)
    },
    onAudio: (base64Audio) => {
      const bytes = estimateBase64Bytes(base64Audio)
      const turn = benDiagnosticsState.currentSpeechTurn
      if (turn) {
        turn.audioChunks += 1
        turn.audioBytes += bytes
        turn.lastAudioAt = safeNow()
      }
      recordBenDiagnostic('audio_chunk', {
        bytes,
        base64Length: typeof base64Audio === 'string' ? base64Audio.length : 0,
        turnAudioChunks: turn?.audioChunks || 0,
      })
    },
    onAudioAlignment: (alignment) => {
      const summary = summarizeAlignment(alignment)
      const turn = benDiagnosticsState.currentSpeechTurn
      if (turn) {
        turn.alignmentEvents += 1
        turn.lastAlignmentTail = summary.tail
        turn.lastAlignmentDurationMs = summary.durationMs
      }
      recordBenDiagnostic('audio_alignment', summary, { forceLog: true })
    },
    onModeChange: ({ mode } = {}) => {
      const previousMode = benDiagnosticsState.mode
      if (previousMode === 'speaking' && mode !== 'speaking') {
        finishCurrentSpeechTurn('mode_change_to_listening')
      }

      benDiagnosticsState.mode = mode || null
      if (mode === 'speaking') startSpeechTurn()

      recordBenDiagnostic('mode_change', { previousMode, nextMode: mode }, { forceLog: true })
    },
    onVadScore: ({ vadScore } = {}) => {
      recordBenDiagnostic('vad_score', { vadScore })
    },
    onStatusChange: (s) => {
      const status = s?.status || s
      benDiagnosticsState.status = status
      recordBenDiagnostic('status_change', { status })
      console.log('[Ben] status', s)
    },
    onConversationMetadata: (metadata) => {
      console.log('[Ben] metadata', metadata)
      const conversationId =
        metadata?.conversation_id ||
        metadata?.conversationId ||
        metadata?.conversation_initiation_client_data?.conversation_id
      if (conversationId) {
        benDiagnosticsState.conversationId = conversationId
        recordBenDiagnostic('conversation_metadata', { conversationId }, { persist: true })
        trackBenEvent('ben_conversation_metadata')
      }
    },
    onAgentToolRequest: (payload) => {
      const toolName = getToolName(payload)
      console.log('[Ben] tool request', toolName, payload)
      trackBenEvent('ben_tool_request', { tool_name: toolName })
    },
    onAgentToolResponse: (payload) => {
      const toolName = getToolName(payload)
      console.log('[Ben] tool response', toolName, payload)
      trackBenEvent('ben_tool_response', { tool_name: toolName })
    },
    onInterruption: (payload) => {
      recordBenDiagnostic('interruption', payload || {}, { warn: true, persist: true })
      console.warn('[Ben] interruption', payload)
      trackBenEvent('ben_interruption')
    },
    onAgentResponseCorrection: (payload) => {
      recordBenDiagnostic(
        'agent_response_correction',
        {
          originalTail: trimText(payload?.original_agent_response || '', 180),
          correctedTail: trimText(payload?.corrected_agent_response || '', 180),
        },
        { warn: true, persist: true }
      )
      console.warn('[Ben] response correction', payload)
      trackBenEvent('ben_response_correction')
    },
    onGuardrailTriggered: () => {
      recordBenDiagnostic('guardrail_triggered', {}, { warn: true, persist: true })
      console.warn('[Ben] guardrail triggered')
      trackBenEvent('ben_guardrail_triggered')
    },
    onDebug: (payload) => {
      recordBenDiagnostic('sdk_debug', {
        debugType: payload?.type || payload?.event?.type || 'unknown',
      })
      console.debug('[Ben] sdk debug', payload)
    },
  })

  useEffect(() => {
    installBenDiagnosticsApi()
    return () => {
      finishCurrentSpeechTurn('unmount')
      persistBenDiagnostics()
      unlockCtxRef.current?.close().catch(() => {})
    }
  }, [])

  const start = useCallback(() => {
    const connectionType = getBenConnectionType()
    const isWebSocket = connectionType === 'websocket'
    const connectionDelay = isWebSocket ? BEN_CONNECTION_DELAY : null

    resetBenDiagnostics({
      agentId: BEN_AGENT_ID,
      connectionType,
      transportSource: connectionType === DEFAULT_CONNECTION_TYPE ? 'default' : 'url_param',
      connectionDelay,
      firstMessageSource: 'agent_config',
      note:
        'Frontend-only diagnostics. Use ?ben_transport=webrtc to test WebRTC transport. VAD callback requires vad_score to be enabled in ElevenLabs agent client_events.',
    })

    // iOS Safari requirement: AudioContext must be resumed synchronously
    // inside the user gesture, before any await. Keep it persisted in a ref
    // so the document stays marked as "audio-enabled" for the session.
    // Fixes the first-message cold-start audio drop (issue #29).
    if (isWebSocket && !unlockCtxRef.current) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext
        if (Ctx) {
          const ctx = new Ctx()
          const src = ctx.createBufferSource()
          src.buffer = ctx.createBuffer(1, 1, 22050)
          src.connect(ctx.destination)
          src.start(0)
          if (ctx.state === 'suspended') ctx.resume()
          unlockCtxRef.current = ctx
        }
      } catch (e) {
        console.warn('[Ben] AudioContext prime failed (non-fatal):', e)
      }
    }

    // Default remains WebSocket for the public path. `?ben_transport=webrtc`
    // enables a controlled WebRTC test for the clipped-ending investigation.
    // WebSocket still keeps the AudioWorklet startup delay; WebRTC skips it
    // so we can test the transport cleanly.
    conv.startSession({
      agentId: BEN_AGENT_ID,
      connectionType,
      ...(isWebSocket && { connectionDelay: BEN_CONNECTION_DELAY }),
      useWakeLock: true,
    })
  }, [conv])

  return {
    start,
    stop: conv.endSession,
    toggleMute: () => conv.setMuted(!conv.isMuted),
    setMuted: conv.setMuted,
    sendText: conv.sendUserMessage,
    noteUserActivity: conv.sendUserActivity,
    status: conv.status,
    statusMessage: conv.message,
    isMuted: conv.isMuted,
    isSpeaking: conv.isSpeaking,
    isListening: conv.isListening,
    mode: conv.mode,
    getOutputVolume: conv.getOutputVolume,
    getInputVolume: conv.getInputVolume,
  }
}
